/**
 * The init config of each profile (spec WP1 "Profiles", D, F, J). Pure: the
 * runtime hooks (context getter, impersonation flag, hostname) come in as
 * functions so tests can build every config without a browser.
 *
 * Every profile sets each remote-fallback option explicitly
 * (`capture_heatmaps`, `capture_performance`, `capture_exceptions`,
 * `disable_session_recording`), so the PostHog project settings never decide
 * for a client.
 */

import type { BeforeSendFn, PostHogConfig } from 'posthog-js';

import {
  STUDIO_DROPPED_ROUTES,
  contextMerge,
  dropRoutes,
  fingerprintKnown,
  hostGuard,
  impersonationGate,
  sampleWebVitals,
  sanitizeUrls,
  scrubPii,
} from './before-send';
import type { App } from './registry/apps';
import { ELEMENT_ATTRIBUTE_IGNORELIST, v3SessionRecording } from './replay';
import { ACQUISITION_PARAMS, LEGACY_URL_PROPERTIES, stripUrlQuery } from './url';

export type AnalyticsMode = 'live' | 'debug' | 'off';
export type Profile = 'full' | 'studio' | 'identity' | 'landing';

const PROFILES: readonly Profile[] = ['full', 'studio', 'identity', 'landing'];

/** `VITE_POSTHOG_PROFILE` → profile; unknown or empty → `undefined` (today's init). */
export function profileFromEnv(v: string | undefined): Profile | undefined {
  const value = (v ?? '').trim();
  return (PROFILES as readonly string[]).includes(value) ? (value as Profile) : undefined;
}

export interface InitOptions {
  apiKey: string;
  apiHost: string;
  uiHost: string;
  /** 'live' sends events, 'debug' logs them to the console (local), 'off' no-ops. */
  mode: AnalyticsMode;
  /**
   * Strip query strings from captured URLs, required when URLs carry operator
   * input. Profile unset: `true` cuts everything after `?`/`#` on four keys,
   * as before. With a profile the extended sanitiser always runs, and
   * `{ keepParams }` keeps the named parameters.
   */
  sanitizeUrls?: boolean | { keepParams: string[] } | undefined;
  /** Unset = today's init, byte-for-byte. */
  profile?: Profile | undefined;
  /** Registered with `environment` and `app_version` when a profile is set. */
  app?: App | undefined;
  /** 12-char commit sha (`__APP_VERSION__`); `local` when absent. */
  appVersion?: string | undefined;
  superProperties?: Record<string, string | number | boolean | null> | undefined;
  /** Merged into every event in `before_send` (A4 dynamic props). Read through a ref. */
  context?: (() => Record<string, unknown>) | undefined;
  /** Hostnames that receive the PostHog tracing headers; ignored without a profile. */
  tracingHeaders?: string[] | undefined;
  /** `'v3'` = the F client replay config; ignored without a profile. */
  replay?: 'off' | 'v3' | undefined;
  bootstrap?: { distinctID: string; sessionID?: string | undefined; isIdentifiedID?: boolean | undefined } | undefined;
  persistence?: 'memory' | undefined;
  /**
   * The app reports its auth (`setImpersonated`) once Clerk has loaded: until
   * then every event is dropped, so an impersonated session sends nothing
   * before its `actor` is known (spec B), and replay waits. Release re-sends
   * the initial `$pageview`. Pass it only where something always calls
   * `setImpersonated`; ignored without a profile.
   */
  awaitAuth?: boolean | undefined;
}

export interface RuntimeHooks {
  getContext: () => Record<string, unknown> | undefined;
  isImpersonated: () => boolean;
  /** Told the name of each event the impersonation gate drops. */
  onBlocked?: ((event: string) => void) | undefined;
  /** Told the name of each event the impersonation gate lets through. */
  onPassed?: ((event: string) => void) | undefined;
  hostname: () => string;
  pathname: () => string;
}

/** Today's init (profile unset). Do not touch: Apptics builds the same crm-app. */
export function legacyConfig(o: InitOptions): Partial<PostHogConfig> {
  return {
    api_host: o.apiHost,
    ui_host: o.uiHost,
    // Modern SDK defaults; `history_change` captures pageviews on History API
    // navigations (react-router BrowserRouter) and pairs `$pageleave`.
    defaults: '2025-05-24',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    // Multi-tenant SaaS: only create person profiles for identified users.
    person_profiles: 'identified_only',
    autocapture: true,
    ...(o.sanitizeUrls
      ? {
          sanitize_properties: (properties: Record<string, unknown>) => {
            for (const key of LEGACY_URL_PROPERTIES) {
              if (key in properties) properties[key] = stripUrlQuery(properties[key]);
            }
            return properties;
          },
        }
      : {}),
  };
}

/**
 * The SDK's own masking of URL query values, ahead of the `before_send`
 * sanitiser: it also covers the URLs posthog-js nests where the sanitiser does
 * not look (web vitals, session entry, replay meta). V3 list screens keep their
 * free-text search in `?q=`. Cost: the SDK masks the click ids too
 * (`gclid=<masked>`), which only the landing needs.
 */
function maskUrlParams(): Partial<PostHogConfig> {
  return {
    mask_personal_data_properties: true,
    custom_personal_data_properties: ['search', 'q', 'email', 'name', 'phone'],
  };
}

const EXCEPTIONS_ON = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
} as const;

function keepParamsOf(o: InitOptions, fallback: readonly string[]): readonly string[] {
  return typeof o.sanitizeUrls === 'object' && o.sanitizeUrls !== null ? o.sanitizeUrls.keepParams : fallback;
}

function bootstrapOf(b: NonNullable<InitOptions['bootstrap']>): NonNullable<PostHogConfig['bootstrap']> {
  return {
    distinctID: b.distinctID,
    ...(b.sessionID ? { sessionID: b.sessionID } : {}),
    ...(b.isIdentifiedID === undefined ? {} : { isIdentifiedID: b.isIdentifiedID }),
  };
}

function identityOptions(o: InitOptions): Partial<PostHogConfig> {
  return {
    ...(o.bootstrap ? { bootstrap: bootstrapOf(o.bootstrap) } : {}),
    ...(o.persistence ? { persistence: o.persistence } : {}),
    ...(o.tracingHeaders && o.tracingHeaders.length > 0 ? { tracing_headers: o.tracingHeaders } : {}),
  };
}

function appAutocapture(): PostHogConfig['autocapture'] {
  return {
    dom_event_allowlist: ['click', 'submit'],
    element_allowlist: ['a', 'button', 'form', 'select', 'label'],
    element_attribute_ignorelist: [...ELEMENT_ATTRIBUTE_IGNORELIST],
  };
}

/** The `before_send` array of a profile, in spec D order. */
export function beforeSendChain(profile: Profile, o: InitOptions, hooks: RuntimeHooks): BeforeSendFn[] {
  const chain: BeforeSendFn[] = [impersonationGate(hooks.isImpersonated, hooks.onBlocked, hooks.onPassed)];
  if (profile === 'landing') chain.push(hostGuard(hooks.hostname));
  chain.push(contextMerge(hooks.getContext));
  chain.push(sanitizeUrls(keepParamsOf(o, profile === 'landing' ? ACQUISITION_PARAMS : [])));
  chain.push(scrubPii);
  if (profile !== 'identity') chain.push(fingerprintKnown);
  if (profile === 'full') chain.push(sampleWebVitals(0.25));
  if (profile === 'studio') chain.push(dropRoutes(STUDIO_DROPPED_ROUTES, hooks.pathname));
  return chain;
}

/** The init config of a profile. */
export function profileConfig(profile: Profile, o: InitOptions, hooks: RuntimeHooks): Partial<PostHogConfig> {
  const base: Partial<PostHogConfig> = {
    api_host: o.apiHost,
    ui_host: o.uiHost,
    defaults: '2026-08-30',
    person_profiles: 'identified_only',
    before_send: beforeSendChain(profile, o, hooks),
  };

  switch (profile) {
    case 'full': {
      const replay = o.replay === 'v3';
      return {
        ...maskUrlParams(),
        ...base,
        capture_pageview: 'history_change',
        capture_pageleave: false,
        autocapture: appAutocapture(),
        mask_all_text: true,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        capture_performance: { web_vitals: true, network_timing: true },
        capture_exceptions: { ...EXCEPTIONS_ON },
        internal_or_test_user_hostname: /^(localhost|127\.0\.0\.1)$/,
        // Waiting for auth: `setImpersonated(false)` starts it (browser.ts).
        disable_session_recording: !replay || o.awaitAuth === true,
        ...(replay ? { session_recording: v3SessionRecording(), enable_recording_console_log: true } : {}),
        ...identityOptions(o),
      };
    }
    case 'studio':
      return {
        ...maskUrlParams(),
        ...base,
        capture_pageview: 'history_change',
        capture_pageleave: false,
        autocapture: appAutocapture(),
        mask_all_text: false,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        capture_performance: false,
        capture_exceptions: { ...EXCEPTIONS_ON },
        internal_or_test_user_hostname: /^(localhost|127\.0\.0\.1)$/,
        disable_session_recording: true,
        // Studio reads no feature flag and shows no survey, tour or chat: none
        // of their requests belong on the editor's boot.
        advanced_disable_flags: true,
        disable_surveys: true,
        disable_product_tours: true,
        disable_conversations: true,
        ...identityOptions(o),
      };
    case 'identity':
      return {
        ...base,
        capture_pageview: 'history_change',
        autocapture: false,
        capture_pageleave: false,
        disable_session_recording: true,
        capture_exceptions: false,
        capture_heatmaps: false,
        capture_performance: false,
        ...maskUrlParams(),
        ...identityOptions(o),
      };
    case 'landing':
      return {
        ...base,
        disable_session_recording: true,
        disable_surveys: true,
        capture_pageleave: true,
        capture_dead_clicks: false,
        capture_heatmaps: true,
        capture_performance: { web_vitals: true },
        capture_exceptions: { ...EXCEPTIONS_ON },
        autocapture: { dom_event_allowlist: ['click', 'submit'], element_allowlist: ['a', 'button', 'form'] },
        ...identityOptions(o),
      };
  }
}
